import type { GridColType } from "@mui/x-data-grid-premium";
import { logError } from "@entrilia/shared/logging";
import type { Maybe } from "@entrilia/shared/types";
import { arraysHaveSameItems } from "@entrilia/shared/utilities/arrayHelper";
import { formatDate } from "@entrilia/shared/utilities/dateUtils";
import { formatMoney, formatMultiplier, formatNumber, formatPercentage } from "@entrilia/shared/utilities/formatters";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { ClientDictionaries } from "../../api/types/clientTypes";
import type {
  DateEntityFieldConfiguration,
  EntityFieldConfiguration,
  EntityFieldType,
  Field,
  FieldCitation,
  FieldConfigurationType,
  FieldIdToValueMap,
  FieldsCitationsMap,
  LookupObjectReference,
  MultiLabelSelectOption,
  ObjectFieldValues,
  ObjectTypeToLookupReferences,
  ObjectWithFieldValuesBase,
  SelectOption,
} from "../../api/types/objectTypes";
import { getSelectOptionsFromDictionary } from "../../utilities/dictionariesHelper";

export type ObjectLayoutSection = "notes" | "file_preview" | "logo" | "tasks";

export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

export const getMultiselectOptionsFromValue = (value: string | string[] | undefined | null) => {
  if (value === undefined || value === null) {
    return [];
  }

  const valueArray = Array.isArray(value) ? value : value.split(fieldsEditSettings.multiselectItemsSeparator);
  return valueArray.filter((item) => Boolean(item.trim()));
};

export const getSelectOptionsForLookup = (lookupReferences: LookupObjectReference[]): MultiLabelSelectOption[] =>
  lookupReferences.map((ref) => ({
    value: ref.objectId,
    label: ref.primaryFieldValue,
    secondaryLabel: ref.secondaryFieldValue,
  }));

export const getLookupOptionDisplayValue = (
  objectId: Maybe<string>,
  lookupReferences: LookupObjectReference[]
): string | undefined => {
  if (!objectId) {
    return undefined;
  }

  const lookupReference = lookupReferences.find((ref) => ref.objectId === objectId);
  if (!lookupReference) {
    return objectId;
  }

  return lookupReference.secondaryFieldValue
    ? `${lookupReference.primaryFieldValue} (${lookupReference.secondaryFieldValue})`
    : lookupReference.primaryFieldValue;
};

export const getLookupReferenceForFieldId = (
  fieldId: string,
  object: ObjectWithFieldValuesBase
): LookupObjectReference | undefined => {
  const objectId = object.fieldValues[fieldId];
  if (typeof objectId !== "string" || !object.lookupObjects) {
    return undefined;
  }

  return object.lookupObjects[fieldId]?.find((ref) => ref.objectId === objectId);
};

export const getObjectSelectLabel = (obj: ObjectFieldValues): string =>
  obj.displayValues.secondaryValue
    ? `${obj.displayValues.primaryValue} (${obj.displayValues.secondaryValue})`
    : obj.displayValues.primaryValue;

export const getCurrencySymbol = (currencyCode: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    })
      .format(0)
      .replace(/[^\p{Sc}]/gu, "");
  } catch (error) {
    logError(error, "getCurrencySymbolByCode");
    return "";
  }
};

export const withRecordCurrency = (
  configuration: EntityFieldConfiguration | undefined,
  fieldValues: FieldIdToValueMap
): EntityFieldConfiguration | undefined => {
  if (configuration?.$type !== "Money" || configuration.currencyCode || !configuration.currencyCodeFieldId) {
    return configuration;
  }

  const currencyCode = fieldValues[configuration.currencyCodeFieldId];
  // Codes come from Business Central as-is, and Intl.NumberFormat throws on anything that is not ISO-shaped.
  return typeof currencyCode === "string" && /^[A-Z]{3}$/i.test(currencyCode)
    ? { ...configuration, currencyCode }
    : configuration;
};

export const formatDateFieldValue = (
  date: Maybe<Date>,
  fieldConfiguration: DateEntityFieldConfiguration | undefined
): string => {
  if (!date) {
    return "";
  }

  if (!fieldConfiguration) {
    return formatDate(date);
  }

  const { dateComponents } = fieldConfiguration;

  if (dateComponents === undefined || dateComponents.length === 0 || dateComponents.length === 3) {
    return formatDate(date);
  }

  if (arraysHaveSameItems(dateComponents, ["Year"])) {
    return formatDate(date, "yyyy");
  }

  if (arraysHaveSameItems(dateComponents, ["Month"])) {
    return formatDate(date, "MMMM");
  }

  if (arraysHaveSameItems(dateComponents, ["Month", "Year"])) {
    return formatDate(date, "MMMM, yyyy");
  }

  if (arraysHaveSameItems(dateComponents, ["Month", "Day"])) {
    return formatDate(date, "MMMM dd");
  }

  return formatDate(date); // Day & Year and Day-only cases are odd and not supported
};

export const formatNumericFieldValue = (
  value: Maybe<number>,
  fieldConfiguration: EntityFieldConfiguration | undefined
): string => (!value || Number.isNaN(value) ? "" : formatNumericValue(value, fieldConfiguration));

export const formatNumericValue = (value: number, fieldConfiguration: EntityFieldConfiguration | undefined): string => {
  if (fieldConfiguration?.$type === "Number") {
    const maximumFractionDigits = fieldConfiguration.precision ?? 0;
    return fieldConfiguration.scaleFormat === "Multiple"
      ? formatMultiplier(value, { maximumFractionDigits })
      : formatNumber(value, { maximumFractionDigits, factor: fieldConfiguration.scaleFormat });
  }

  if (fieldConfiguration?.$type === "Money") {
    const maximumFractionDigits = fieldConfiguration.precision ?? 0;
    const minimumFractionDigits = fieldConfiguration.scaleFormat ? undefined : maximumFractionDigits;
    return fieldConfiguration.scaleFormat === "Multiple"
      ? formatMultiplier(value, { maximumFractionDigits })
      : fieldConfiguration.currencyCode
        ? formatMoney(value, fieldConfiguration.currencyCode, {
            maximumFractionDigits,
            factor: fieldConfiguration.scaleFormat,
          })
        : formatNumber(value, {
            minimumFractionDigits,
            maximumFractionDigits,
            factor: fieldConfiguration.scaleFormat,
          });
  }

  if (fieldConfiguration?.$type === "Percent") {
    const maximumFractionDigits = fieldConfiguration.precision ?? 0;
    return formatPercentage(0.01 * value, { maximumFractionDigits });
  }

  return formatNumber(value);
};

export const fieldsEditSettings = {
  multiselectItemsSeparator: ";",
};

export const getInvalidFieldValueError = (
  fieldId: string,
  fieldType: FieldConfigurationType | undefined,
  value: unknown
) => `Invalid value in ${fieldType ?? ""} field ${fieldId}: ${stringifyValue(value)} (${typeof value})`;

export const mapLookupOptionsToSelectOptions = (
  lookupOptions: ObjectTypeToLookupReferences,
  objectFields: Field[],
  fieldId: string
): SelectOption[] => {
  const filterField = objectFields.find((field) => field.id === fieldId);
  if (filterField === undefined) {
    return [];
  }

  const targetObjectType =
    filterField.configuration?.$type === "Lookup" ? filterField.configuration.objectType : undefined;
  if (targetObjectType === undefined) {
    return [];
  }

  const lookupObjectReferences = lookupOptions[targetObjectType];
  if (lookupObjectReferences === undefined) {
    return [];
  }

  return lookupObjectReferences.map((r) => ({ value: r.objectId, label: r.primaryFieldValue }));
};

// When undefined, the field should be rendered via renderEditCell in editable grids
export const fieldTypeToGridColTypeMap: Record<EntityFieldType, GridColType | undefined> = {
  Text: "string",
  Email: "string",
  Phone: "string",
  Url: "string",
  Number: "number",
  Money: "number",
  Percent: "number",
  Date: "date",
  DateTime: "dateTime",
  Checkbox: "boolean",
  Select: "singleSelect",
  UserDefinedOptionsSelect: "singleSelect",
  Guid: "string",
  MultiSelect: undefined,
  UserDefinedOptionsMultiSelect: undefined,
  Lookup: undefined,
  MultiLookup: undefined,
};

export const getValueOptionsForSingleSelectField = (
  fieldConfiguration: EntityFieldConfiguration | undefined,
  clientDictionaries: ClientDictionaries,
  lookupOptions: ObjectTypeToLookupReferences,
  params?: { useLabelAsValue: boolean }
): { value: string; label: string }[] => {
  const useLabelAsValue = params?.useLabelAsValue === true;

  if (fieldConfiguration?.$type === "Select") {
    const dictionaryType = fieldConfiguration.fieldType;
    return getSelectOptionsFromDictionary(dictionaryType, clientDictionaries).map(({ value, label }) => ({
      label,
      value: useLabelAsValue ? label : value,
    }));
  }

  if (fieldConfiguration?.$type === "UserDefinedOptionsSelect") {
    return fieldConfiguration.userDefinedOptions.map(({ value, label }) => ({
      label,
      value: useLabelAsValue ? label : value,
    }));
  }

  if (fieldConfiguration?.$type === "Lookup") {
    const targetObjectType = fieldConfiguration.objectType;
    const lookupReferences = lookupOptions[targetObjectType];
    if (lookupReferences === undefined || lookupReferences.length === 0) {
      return [];
    }

    return lookupReferences.map((ref) => {
      const label = ref.secondaryFieldValue
        ? `${ref.primaryFieldValue} (${ref.secondaryFieldValue})`
        : ref.primaryFieldValue;

      return {
        value: useLabelAsValue ? label : ref.objectId,
        label,
      };
    });
  }

  return [];
};

const lowFieldConfidenceThreshold = 0.3;

export const isFieldConfidenceLow = (confidence: number | undefined): boolean =>
  confidence !== undefined && confidence < lowFieldConfidenceThreshold;

export const isFieldFilterable = (field: Field): boolean => field.type !== "Guid";

export interface DocumentCitation extends FieldCitation {
  key: string;
}

export const collectFieldCitations = (fieldsCitations: FieldsCitationsMap | undefined): DocumentCitation[] =>
  fieldsCitations ? Object.entries(fieldsCitations).map(([fieldId, citation]) => ({ key: fieldId, ...citation })) : [];
